import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { Validators as RegExps } from '@core/constants/validators';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { PcpSelectionModalComponent } from '@core/components/pcp-selection-modal/pcp-selection-modal.component';
import { catchError, takeUntil } from 'rxjs/operators';
import { of, ReplaySubject } from 'rxjs';
import { PcpService } from '@core/data/pcp.service';
import { PcpListResponse } from '@core/interfaces/api-responses';
import { PcpItem } from "@core/interfaces/entities";

@Component({
  selector: 'app-pcp-search',
  templateUrl: './pcp-search.component.html',
  styleUrls: ['./pcp-search.component.scss'],
})
export class PcpSearchComponent implements OnDestroy {

  @Input() lobCode = 'HFSG';
  @Input() companyCode = 30;
  @Output() pcpSelected = new EventEmitter();
  public searchForm: UntypedFormGroup;
  public readonly alphaSpaceRegEx = { S: { pattern: RegExps.alphaSpaceRegEx } };
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  private modalRef: NgbModalRef;

  constructor(private fb: UntypedFormBuilder, private modalService: NgbModal, private pcpService: PcpService) {
    this.searchForm = this.fb.group({
      zip: [null, Validators.required],
      pcpName: [null],
    });
  }

  get invalidZip() {
    return this.searchForm.invalid && (this.searchForm.dirty || this.searchForm.touched);
  }

  openPcpSelectionModal() {
    if (this.searchForm.invalid) {
      return;
    }

    this.modalRef = this.modalService.open(PcpSelectionModalComponent, {
      centered: true,
      size: 'lg',
      scrollable: true,
      windowClass: 'force-full-screen',
      backdrop: false,
    });

    this.searchProvider();

    this.modalRef.componentInstance.pageChanged
      .pipe(takeUntil(this.destroyed$))
      .subscribe((paginationQuery: { page: number, pageSize: number }) => {
        this.searchProvider(paginationQuery);
      });

    this.modalRef.componentInstance.selectionMade
      .pipe(takeUntil(this.destroyed$))
      .subscribe((pcpInfo: PcpItem) => {
        this.pcpSelected.emit(pcpInfo);
      });
  }

  searchProvider(paginationQuery: { page?: number, pageSize?: number } = {}) {
    const formValue = this.searchForm.value;
    this.pcpService.listPcp({
      ...paginationQuery,
      ...{
        zipCode: formValue.zip,
        query: formValue.pcpName || '',
        lob: this.lobCode,
        company: this.companyCode,
      },
    })
      .pipe(
        takeUntil(this.destroyed$),
        catchError(() => of({ data: { totalResults: 0, results: { providers: [] } } })))
      .subscribe((response: PcpListResponse) => this.modalRef.componentInstance.pcpProviders = {
        total: response.data.totalResults,
        items: response.data.results.providers,
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
